import React, { createContext, useContext, useState, useCallback } from 'react';
import * as LeaveService from '../services/leaveService';

const LeaveContext = createContext();

export const useLeave = () => useContext(LeaveContext);

export const LeaveProvider = ({ children }) => {
     const [myLeaves, setMyLeaves] = useState([]);
     const [pendingLeaves, setPendingLeaves] = useState([]);
     const [loading, setLoading] = useState(false);
     const [error, setError] = useState(null);

     // helper to normalize API response -> array
     const normalizeList = (resp) => {
          if (!resp) return [];
          if (Array.isArray(resp)) return resp;
          if (resp.data && Array.isArray(resp.data)) return resp.data;
          // sometimes backend returns { status: true, data: {...} } single object
          if (resp.data && !Array.isArray(resp.data)) return [resp.data];
          // if resp itself is an object representing a single leave
          return typeof resp === 'object' ? [resp] : [];
     };

     const fetchMyLeaves = useCallback(async (param = 'me') => {
          setLoading(true);
          setError(null);
          try {
               const json = await LeaveService.getMyLeaves(param);
               // accept both array or wrapped {status,data}
               const items = normalizeList(json);
               setMyLeaves(items || []);
               return items;
          } catch (e) {
               setError(e);
               setMyLeaves([]);
               throw e;
          } finally {
               setLoading(false);
          }
     }, []);

     const fetchPendingLeaves = useCallback(async () => {
          setLoading(true);
          setError(null);
          try {
               const json = await LeaveService.getPendingLeaves();
               const items = normalizeList(json);
               setPendingLeaves(items || []);
               return items;
          } catch (e) {
               setError(e);
               setPendingLeaves([]);
               throw e;
          } finally {
               setLoading(false);
          }
     }, []);

     const apply = async (payload) => {
          setLoading(true);
          try {
               // if leave_id exists, call update endpoint; otherwise create new leave
               const json = payload?.leave_id
                    ? await LeaveService.updateLeave(payload.leave_id, payload)
                    : await LeaveService.applyLeave(payload);
               if (payload?.emp_code) await fetchMyLeaves(payload.emp_code);
               else await fetchMyLeaves('me').catch(() => { });
               return json;
          } catch (e) {
               setError(e);
               throw e;
          } finally {
               setLoading(false);
          }
     };
     const approve = async (id, payload = {}) => {
          setLoading(true);
          try {
               const json = await LeaveService.approveLeave(id, payload);
               await fetchPendingLeaves();
               await fetchMyLeaves('me').catch(() => { });
               return json;
          } catch (e) {
               setError(e);
               throw e;
          } finally {
               setLoading(false);
          }
     };

     const reject = async (id, payload = {}) => {
          setLoading(true);
          try {
               const json = await LeaveService.rejectLeave(id, payload);
               await fetchPendingLeaves();
               await fetchMyLeaves('me').catch(() => { });
               return json;
          } catch (e) {
               setError(e);
               throw e;
          } finally {
               setLoading(false);
          }
     };

     const pullBack = async (id) => {
          setLoading(true);
          try {
               const json = await LeaveService.pullBackLeave(id);
               await fetchMyLeaves('me').catch(() => { });
               return json;
          } catch (e) {
               setError(e);
               throw e;
          } finally {
               setLoading(false);
          }
     };

     return (
          <LeaveContext.Provider value={{
               myLeaves,
               pendingLeaves,
               loading,
               error,
               fetchMyLeaves,
               fetchPendingLeaves,
               apply,
               approve,
               reject,
               pullBack
          }}>
               {children}
          </LeaveContext.Provider>
     );
};

export default LeaveContext;